import { useChat } from '@ai-sdk/react';
// import type { Attachment, LlmUIMessage as LlmChatMessage } from '@azymmetric/shared/types/llm.types';
import type { LlmUIMessage as LlmChatMessage } from '@azymmetric/shared/types/llm.types';
import { Tile } from '@components/shared/tile.component';
import { Button } from '@components/ui/button';
import { config } from '@config';
import { useBuildHeaders } from '@hooks/api.hook';
import { cn, generateUUID } from '@lib/utils';
import { useArtifactStore } from '@stores/artifact.store';
import { llmModelStore } from '@stores/llm-model.store';
import { DefaultChatTransport } from 'ai';
import { GripVertical } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Group, Panel, Separator, useDefaultLayout } from 'react-resizable-panels';
import { useStore } from 'zustand';
import { ArtifactContainer } from '../artifacts/artifact-container.component';
import { ChatInput } from './chat-input.component';
import { ChatMessages } from './chat-messages.component';

type ChatProps = {
  id: string;
  initialMessages: LlmChatMessage[];
  isPending: boolean;
};

export const Chat: React.FC<ChatProps> = (props) => {
  const { id, initialMessages, isPending } = props;

  const { buildHeaders } = useBuildHeaders();
  const { selectedModel } = useStore(llmModelStore);
  const { artifact, setArtifact } = useArtifactStore();

  const [input, setInput] = useState<string>('');
  const panelLayout = useDefaultLayout({
    id: 'research-chat-artifact-layout',
    panelIds: ['chat', 'artifact'],
  });

  useEffect(() => {
    return () => {
      setArtifact(null);
    };
  }, [setArtifact]);

  const { messages, setMessages, sendMessage, status, stop, regenerate } = useChat<LlmChatMessage>({
    id,
    messages: initialMessages,
    generateId: generateUUID,
    transport: new DefaultChatTransport({
      api: `${config.api.baseUrl}/v1/chats`,
      credentials: 'include',
      headers: async () => await buildHeaders(),
      prepareSendMessagesRequest: ({ id, messages, body }) => {
        return {
          body: {
            id,
            messages,
            model: selectedModel,
            ...body,
          },
        };
      },
    }),
  });

  const chatPanel = (
    <div
      className={cn(
        'flex h-full min-h-0 min-w-0 flex-col overscroll-contain',
        !artifact && 'w-full max-w-[1000px] flex-1',
      )}
    >
      <Tile
        header={{
          title: 'Research',
          className: 'p-4',
          actions: (
            <Button
              onClick={() => {
                setMessages([]);
                setArtifact(null);
              }}
              variant="link"
            >
              Reset Chat
            </Button>
          ),
        }}
        isLoading={isPending}
        className="flex h-full min-h-0 min-w-0 flex-1 flex-col touch-pan-y overscroll-behavior-contain p-0"
        contentClassName="relative flex h-full min-h-0 flex-1 flex-col overflow-hidden"
      >
        <ChatMessages
          chatId={id}
          messages={messages}
          regenerate={regenerate}
          status={status}
          setMessages={setMessages}
        />
        <div className="flex w-full shrink-0">
          <ChatInput
            chatId={id}
            messages={messages}
            input={input}
            setInput={setInput}
            sendMessage={sendMessage}
            status={status}
            stop={stop}
          />
        </div>
      </Tile>
    </div>
  );

  if (!artifact) {
    return <section className="flex h-full min-h-0 items-stretch p-4">{chatPanel}</section>;
  }

  return (
    <section className="h-full min-h-0 w-full p-4">
      <Group
        className="h-full min-h-0 w-full"
        defaultLayout={panelLayout.defaultLayout}
        onLayoutChanged={panelLayout.onLayoutChanged}
        orientation="horizontal"
      >
        <Panel
          defaultSize={48}
          id="chat"
          minSize={28}
        >
          {chatPanel}
        </Panel>
        <Separator className="group flex w-3 items-center justify-center outline-none">
          <div className="flex h-16 w-1.5 items-center justify-center rounded-full bg-transparent transition-colors group-hover:bg-gray-200">
            <GripVertical className="size-4 text-gray-400" />
          </div>
        </Separator>
        <Panel
          defaultSize={52}
          id="artifact"
          minSize={32}
        >
          <ArtifactContainer />
        </Panel>
      </Group>
    </section>
  );
};
